// /app/frontend/src/pages/ConversationsV2/CompanyPickers.jsx
// ──────────────────────────────────────────────────────────────────
// Extracted from ConversationsV2Page.jsx (iter131 refactor).
// Owns the shared `useCompanyPicker` hook, the `CompanyList` widget,
// and the two modals that consume them: `InitiateModal` (Op 1 +
// Op 2) and `AddCompanyModal` (Op 3).
//
// Visual contract is locked to the legacy WhatsApp green/white style
// the user explicitly requested — green pill ADD, outlined pill
// INVITE.
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import Modal from "@/pages/ConversationsV2/Modal";
import { useT } from "@/lib/i18n";

const COMPANY_PAGE_SIZE = 50;

export function useCompanyPicker({ conversationId, query }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const reqIdRef = useRef(0);

  // Reset + load first page whenever the search/mode changes.
  useEffect(() => {
    const my = ++reqIdRef.current;
    setLoading(true);
    setItems([]);
    setHasMore(false);
    const t = setTimeout(() => {
      const params = new URLSearchParams({
        q: query.trim(),
        limit: String(COMPANY_PAGE_SIZE),
        offset: "0",
      });
      if (conversationId) params.set("conversation_id", conversationId);
      api.get(`/conv/companies/search?${params.toString()}`)
        .then((r) => {
          if (my !== reqIdRef.current) return;
          setItems(r.data?.items || []);
          setHasMore(Boolean(r.data?.has_more));
        })
        .catch(() => {
          if (my !== reqIdRef.current) return;
          setItems([]);
          setHasMore(false);
        })
        .finally(() => {
          if (my !== reqIdRef.current) return;
          setLoading(false);
        });
    }, 200);
    return () => clearTimeout(t);
  }, [conversationId, query]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    const my = reqIdRef.current;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({
        q: query.trim(),
        limit: String(COMPANY_PAGE_SIZE),
        offset: String(items.length),
      });
      if (conversationId) params.set("conversation_id", conversationId);
      const r = await api.get(`/conv/companies/search?${params.toString()}`);
      if (my !== reqIdRef.current) return;
      const next = r.data?.items || [];
      const seen = new Set(items.map((c) => c.CustomerID));
      const fresh = next.filter((c) => !seen.has(c.CustomerID));
      setItems((prev) => [...prev, ...fresh]);
      setHasMore(Boolean(r.data?.has_more));
    } catch {
      /* soft-fail — user can scroll again */
    } finally {
      if (my === reqIdRef.current) setLoadingMore(false);
    }
  }, [conversationId, hasMore, items, loading, loadingMore, query]);

  return { items, loading, loadingMore, hasMore, loadMore };
}


export function CompanyList({ testId, listHandlers, loading, loadingMore, hasMore, items, onLoadMore, emptyText, renderAction }) {
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) onLoadMore();
    }, { rootMargin: "120px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, onLoadMore]);

  return (
    <div
      {...listHandlers}
      data-testid={testId}
      className="max-h-[360px] overflow-y-auto border border-slate-200 rounded-sm"
    >
      {loading && items.length === 0 && (
        <div className="p-3 text-xs text-slate-400 text-center" data-testid={`${testId}-loading`}>Loading…</div>
      )}
      {!loading && items.length === 0 && (
        <div className="p-3 text-xs text-slate-400 text-center" data-testid={`${testId}-empty`}>{emptyText}</div>
      )}
      {items.map((c) => (
        <div
          key={c.CustomerID}
          className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 hover:bg-slate-50"
          data-testid={`${testId}-row-${c.CustomerID}`}
        >
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-slate-900 truncate">{c.company_name || "—"}</div>
          </div>
          {renderAction(c)}
        </div>
      ))}
      {hasMore && (
        <div ref={sentinelRef} className="p-3 text-center text-[11px] text-slate-400" data-testid={`${testId}-sentinel`}>
          {loadingMore ? "Loading more…" : "Scroll for more"}
        </div>
      )}
    </div>
  );
}


export function InitiateModal({ onClose, onInitiate, onRequest }) {
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const t = useT();
  const { items, loading, loadingMore, hasMore, loadMore } = useCompanyPicker({ conversationId: null, query: q });
  return (
    <Modal title={t("convv2.modal_initiate_title")} onClose={onClose} testId="conv-initiate-modal">
      <p className="text-xs text-slate-500 mb-2">
        {t("convv2.modal_initiate_hint")}
      </p>
      <input
        type="text" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={t("convv2.picker_search_placeholder")}
        data-testid="conv-initiate-search"
        className="w-full px-3 py-2 text-sm rounded-sm border border-slate-300 focus:border-blue-500 focus:outline-none mb-2"
        autoFocus
      />
      <CompanyList
        testId="conv-initiate-list"
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        items={items}
        onLoadMore={loadMore}
        emptyText={q.trim() ? "No matching companies" : "No related companies available"}
        renderAction={(c) => {
          const isAdd = c.action === "add";
          return (
            <button
              disabled={busyId !== null}
              onClick={async () => {
                setBusyId(c.CustomerID);
                try {
                  if (isAdd) await onInitiate(c.CustomerID);
                  else await onRequest(c.CustomerID);
                } finally { setBusyId(null); }
              }}
              className={`shrink-0 h-7 px-3 text-[11px] font-bold uppercase tracking-wider rounded-full hover:brightness-95 transition disabled:opacity-50 ${
                isAdd ? "text-white" : "border border-slate-300 text-slate-700 bg-white"
              }`}
              style={isAdd ? { background: "#00a884" } : undefined}
              data-testid={`conv-initiate-${isAdd ? "add" : "invite"}-${c.CustomerID}`}
            >
              {busyId === c.CustomerID ? "…" : (isAdd ? "Add" : "Invite")}
            </button>
          );
        }}
      />
    </Modal>
  );
}


export function AddCompanyModal({ conversationId, onClose, onAdd, onRequest }) {
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const t = useT();
  const { items, loading, loadingMore, hasMore, loadMore } = useCompanyPicker({ conversationId, query: q });
  return (
    <Modal title={t("convv2.modal_add_company_title")} onClose={onClose} testId="conv-add-company-modal">
      <p className="text-xs text-slate-500 mb-2">
        {t("convv2.modal_add_company_hint")}
      </p>
      <input
        type="text" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={t("convv2.picker_search_placeholder")}
        data-testid="conv-add-company-search"
        className="w-full px-3 py-2 text-sm rounded-sm border border-slate-300 focus:border-blue-500 focus:outline-none mb-2"
        autoFocus
      />
      <CompanyList
        testId="conv-add-company-list"
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        items={items}
        onLoadMore={loadMore}
        emptyText={q.trim() ? "No matching companies" : "No companies available to add"}
        renderAction={(c) => {
          const isAdd = c.action === "add";
          return (
            <button
              disabled={busyId !== null}
              onClick={async () => {
                setBusyId(c.CustomerID);
                try {
                  if (isAdd) await onAdd(c.CustomerID);
                  else await onRequest(c.CustomerID);
                } finally { setBusyId(null); }
              }}
              className={`shrink-0 h-7 px-3 text-[11px] font-bold uppercase tracking-wider rounded-full hover:brightness-95 transition disabled:opacity-50 ${
                isAdd ? "text-white" : "border border-slate-300 text-slate-700 bg-white"
              }`}
              style={isAdd ? { background: "#00a884" } : undefined}
              data-testid={`conv-add-company-${isAdd ? "add" : "invite"}-${c.CustomerID}`}
            >
              {busyId === c.CustomerID ? "…" : (isAdd ? "Add" : "Invite")}
            </button>
          );
        }}
      />
    </Modal>
  );
}
